<template>
  <div class="zone-leakage-detail">
    <!-- 搜索区域 -->
    <a-card :bordered="false" class="search-card">
      <template slot="title">
        <span style="font-weight: bold;">漏损自动统计分析 / {{ zoneName }}</span>
      </template>
      <template slot="extra">
        <a-button @click="$emit('back')" type="primary">返回</a-button>
      </template>
      <div class="table-page-search-wrapper">
        <a-form layout="horizontal">
          <a-row :gutter="48">
            <a-col :md="8" :sm="24">
              <a-form-item label="查询时间" :labelCol="{ span: 5 }" :wrapperCol="{ span: 19 }">
                <a-range-picker v-model="query.dateRange" style="width: 100%" format="YYYY-MM-DD"
                  :placeholder="['开始时间', '结束时间']" />
              </a-form-item>
            </a-col>
            <a-col :md="16" :sm="24">
              <a-button type="primary" @click="handleQuery">查询</a-button>
              <a-button style="margin-left: 8px" @click="handleReset">重置</a-button>
              <a-button type="primary" style="margin-left: 8px" @click="handleExport">导出</a-button>
            </a-col>
          </a-row>
        </a-form>
      </div>
    </a-card>

    <!-- 表格区域 -->
    <a-card :bordered="false" class="table-card">
      <div class="table-title">{{ zoneName }}漏损明细</div>
      <a-table :columns="columns" :data-source="tableData" :pagination="false" :loading="loading" rowKey="date"
        :scroll="{ x: 'max-content' }" bordered size="middle">
      </a-table>
    </a-card>
  </div>
</template>

<script>
import moment from 'moment'
import axios from 'axios'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import storage from 'store'

export default {
  name: 'ZoneLeakageDetail',
  props: {
    record: {
      type: Object,
      default: () => ({})
    },
    initialDateRange: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      loading: false,
      query: {
        dateRange: []
      },
      tableData: [],
      // 动态列定义
      columns: []
    }
  },
  computed: {
    zoneName () {
      const name = this.record.zoneName || this.record.areaName
      const level = this.record.level || this.record.areaLevel || '三级'
      return name ? `${level}分区-${name}` : '分区漏损明细'
    }
  },
  created () {
    // 初始化时间范围
    // 优先使用父组件传递的 initialDateRange，否则默认最近7天
    if (this.initialDateRange && this.initialDateRange.length === 2) {
      this.query.dateRange = this.initialDateRange
    } else {
      this.query.dateRange = [moment().subtract(6, 'days'), moment()]
    }
    this.handleQuery()
  },
  methods: {
    moment,
    handleQuery () {
      // 校验
      if (!this.query.dateRange || this.query.dateRange.length !== 2) {
        this.$message.warning('请先选择查询时间')
        return
      }

      this.loading = true
      const params = {
        zoneId: this.record.id || this.record.zoneId, // 优先取 id
        bgeinDate: this.query.dateRange[0].format('YYYY-MM-DD'),
        endDate: this.query.dateRange[1].format('YYYY-MM-DD')
      }

      this.$http.get('/zone-metering/leakage/level/rule/select/zone/date', { params }).then(({ data }) => {
        console.log('分区漏损明细数据:', data)
        if (data) {
          // 如果后端返回了表头配置，在这里调用 buildColumns(data.columns)
          // 目前假设数据直接在 data 或者是 data.list
          const list = Array.isArray(data) ? data : (data.list || [])

          // 尝试根据返回数据构建动态列
          if (list.length > 0) {
            this.buildColumns(list[0])
            this.tableData = this.processTableData(list)
          } else {
            this.tableData = []
          }
        } else {
          this.tableData = []
        }
      }).catch(err => {
        console.error('查询详情失败:', err)
        this.$message.error('查询详情失败')
      }).finally(() => {
        this.loading = false
      })
    },
    handleReset () {
      if (this.initialDateRange && this.initialDateRange.length === 2) {
        this.query.dateRange = this.initialDateRange
      } else {
        this.query.dateRange = [moment().subtract(6, 'days'), moment()]
      }
      this.handleQuery()
    },
    handleExport () {
      this.loading = true
      const token = storage.get(ACCESS_TOKEN)
      const params = {
        zoneId: this.record.id || this.record.zoneId,
        bgeinDate: this.query.dateRange[0].format('YYYY-MM-DD'),
        endDate: this.query.dateRange[1].format('YYYY-MM-DD')
      }

      axios({
        url: process.env.VUE_APP_API_BASE_URL + 'zone-metering/leakage/level/rule/zone/export',
        method: 'get',
        params: params,
        responseType: 'blob',
        headers: {
          Authorization: token
        }
      }).then(res => {
        const blob = new Blob([res.data], { type: 'application/vnd.ms-excel' })
        const fileName = '分区漏损明细.xlsx'
        if ('download' in document.createElement('a')) { // 非IE下载
          const elink = document.createElement('a')
          elink.download = fileName
          elink.style.display = 'none'
          elink.href = URL.createObjectURL(blob)
          document.body.appendChild(elink)
          elink.click()
          URL.revokeObjectURL(elink.href) // 释放URL 对象
          document.body.removeChild(elink)
        } else { // IE10+下载
          navigator.msSaveBlob(blob, fileName)
        }
        this.$message.success('导出成功')
      }).catch(err => {
        console.error('导出失败:', err)
        this.$message.error('导出失败')
      }).finally(() => {
        this.loading = false
      })
    },
    // 构建表格列
    buildColumns (firstRow) {
      if (!firstRow) return

      // 1. 基础列
      const baseColumns = [
        {
          title: '时间',
          dataIndex: 'date', // 对应 JSON 中的 date
          width: 120,
          fixed: 'left',
          align: 'center'
        },
        {
          title: '供水量',
          dataIndex: 'supplyWater',
          width: 100,
          align: 'center'
        },
        {
          title: '售水量 (m³)',
          dataIndex: 'sellWater', // 对应 JSON 中的 sellWater
          width: 120,
          align: 'center'
        },
        {
          title: '漏损量 (m³)',
          dataIndex: 'leakageWater',
          width: 120,
          align: 'center'
        },
        {
          title: '综合漏损率 (%)',
          dataIndex: 'comprehensiveLeakage', // 对应 JSON 中的 comprehensiveLeakage
          width: 120,
          align: 'center'
        }
      ]

      // 2. 动态进水表列 (intakeWaterGauge)
      const intakeChildren = []
      let hasIntake = false
      if (firstRow.intakeWaterGauge && Array.isArray(firstRow.intakeWaterGauge) && firstRow.intakeWaterGauge.length > 0) {
        hasIntake = true
        firstRow.intakeWaterGauge.forEach((item, index) => {
          intakeChildren.push({
            title: item.waterName || `进水表${index + 1}`,
            dataIndex: `intake_${index}`,
            width: 120,
            align: 'center'
          })
        })
        // 有进水表时才添加合计
        intakeChildren.push({
          title: '合计',
          dataIndex: 'intakeWaterGaugeCount',
          width: 100,
          align: 'center'
        })
      }

      const inflowColumns = hasIntake ? {
        title: '进水表',
        children: intakeChildren
      } : null

      // 3. 动态出水表列 (exitWaterGauge)
      const exitChildren = []
      let hasExit = false
      if (firstRow.exitWaterGauge && Array.isArray(firstRow.exitWaterGauge) && firstRow.exitWaterGauge.length > 0) {
        hasExit = true
        firstRow.exitWaterGauge.forEach((item, index) => {
          exitChildren.push({
            title: item.waterName || `出水表${index + 1}`,
            dataIndex: `exit_${index}`,
            width: 120,
            align: 'center'
          })
        })
        // 有出水表时才添加合计
        exitChildren.push({
          title: '合计',
          dataIndex: 'exitWaterGaugeCount',
          width: 100,
          align: 'center'
        })
      }

      const outflowColumns = hasExit ? {
        title: '出水表',
        children: exitChildren
      } : null

      // 4. 用水表列 (含 DMA 表 和 平行大用户)
      const waterMeterChildren = []

      // 4.1 DMA 水表 (dmaWaterGauge) - 用户提到 dmaWaterGauge
      // 如果 dmaWaterGauge 里的内容是动态的
      if (firstRow.dmaWaterGauge && Array.isArray(firstRow.dmaWaterGauge)) {
        firstRow.dmaWaterGauge.forEach((item, index) => {
          waterMeterChildren.push({
            title: `${item.waterName} (DMA)`,
            dataIndex: `dma_${index}`,
            width: 150,
            align: 'center'
          })
        })
      }

      // 4.2 DMA 水量合计
      waterMeterChildren.push({
        title: 'DMA水量合计',
        dataIndex: 'dmaWaterGaugeCount',
        width: 120,
        align: 'center'
      })

      // 4.3 平行大用户 (固定结构)
      const bigUserColumns = {
        title: '平行大用户',
        children: [
          { title: '平行大用户水合计 (吨)', dataIndex: 'bigUserWaterCount', width: 150, align: 'center' },
          { title: '总平行大用户数 (户)', dataIndex: 'bigUserCount', width: 150, align: 'center' },
          { title: '抄见平行用户数 (户)', dataIndex: 'redactBigUserCount', width: 150, align: 'center' }, // 假设 redact 是抄见
          { title: '未抄见平行大用户数', dataIndex: 'noRedactBigUserCount', width: 150, align: 'center' }, // 假设 noRedact 是未抄见
          { title: '平行表抄见率', dataIndex: 'bigUserRedactPercentage', width: 120, align: 'center' },
          // { title: '合计', dataIndex: 'bigUser_finalTotal', width: 100, align: 'center' } // JSON 中似乎没有这个额外的合计字段，暂且省略或复用 bigUserWaterCount?
        ]
      }
      waterMeterChildren.push(bigUserColumns)

      const waterMeterColumns = {
        title: '用水表',
        children: waterMeterChildren
      }

      // 组装所有列
      this.columns = [
        ...baseColumns,
        inflowColumns,
        outflowColumns,
        waterMeterColumns
      ].filter(Boolean)

      // 移除最后一列的宽度，让其自适应以撑满容器，防止固定列右侧出现空白
      if (this.columns.length > 0) {
        const removeLastWidth = (cols) => {
          const lastCol = cols[cols.length - 1]
          if (lastCol.children && lastCol.children.length > 0) {
            removeLastWidth(lastCol.children)
          } else {
            // 保留一个最小宽度，防止内容过少时被压缩太小
            // lastCol.width = undefined 
            // 在 Ant Design Vue 中，如果不设置 width，列会自适应
            delete lastCol.width
          }
        }
        removeLastWidth(this.columns)
      }
    },
    // 处理数据，将嵌套数组拍平
    processTableData (list) {
      return list.map(item => {
        const row = { ...item }

        // 处理进水表 (intakeWaterGauge)
        if (item.intakeWaterGauge && Array.isArray(item.intakeWaterGauge)) {
          item.intakeWaterGauge.forEach((gauge, index) => {
            row[`intake_${index}`] = gauge.waterCount
          })
        }

        // 处理出水表 (exitWaterGauge)
        if (item.exitWaterGauge && Array.isArray(item.exitWaterGauge)) {
          item.exitWaterGauge.forEach((gauge, index) => {
            row[`exit_${index}`] = gauge.waterCount
          })
        }

        // 处理 DMA 表 (dmaWaterGauge)
        if (item.dmaWaterGauge && Array.isArray(item.dmaWaterGauge)) {
          item.dmaWaterGauge.forEach((gauge, index) => {
            row[`dma_${index}`] = gauge.waterCount
          })
        }

        return row
      })
    },
  }
}
</script>

<style lang="less" scoped>
.zone-leakage-detail {
  .search-card {
    margin-bottom: 16px;
  }

  .table-card {
    .table-title {
      text-align: center;
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 16px;
    }
  }
}
</style>